import { useState } from 'react';
import { Link } from 'react-router-dom';
import { App, Button, Modal, Popconfirm, Select, Space, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, FilterOutlined, SafetyCertificateOutlined, TagsOutlined, UnorderedListOutlined } from '@ant-design/icons';
import type { BulkSelection, FieldDefinitionDto } from '@crm/shared';
import { useBulkDelete, useBulkLists, useBulkSegments, useBulkTags, useBulkUpdate, useCreateList, useCreateSegment, useCreateTag, useLists, useSegments, useTags } from '../features/audience/api';
import { errorMessage } from '../api/client';
import { useVerifySelection } from '../features/verification/api';
import { plural } from '../utils/format';
import { MassEditModal } from './MassEditModal';
import { CreatableSelect } from './CreatableSelect';

interface Props {
  selection: BulkSelection;
  count: number;
  contactFields: FieldDefinitionDto[];
  /** Called after an action succeeds, with which one ran (deleted contacts can't stay selected). */
  onDone: (action: BulkAction) => void;
}

export type BulkAction = 'tags' | 'lists' | 'segments' | 'fields' | 'verify' | 'delete';

type Mode = 'addTags' | 'removeTags' | 'addList' | 'removeList' | 'addSegment' | 'removeSegment' | 'setFields' | null;

/** Actions applied to the current selection (explicit ids or everything matching the view). */
export function BulkActionsBar({ selection, count, contactFields, onDone }: Props) {
  const { message } = App.useApp();
  const [mode, setMode] = useState<Mode>(null);
  const tags = useTags();
  const lists = useLists();
  const createTag = useCreateTag();
  const createList = useCreateList();
  const segments = useSegments();
  const createSegment = useCreateSegment();
  const bulkSegments = useBulkSegments();
  const bulkTags = useBulkTags();
  const bulkLists = useBulkLists();
  const bulkUpdate = useBulkUpdate();
  const bulkDelete = useBulkDelete();
  const verifySel = useVerifySelection();
  const [picked, setPicked] = useState<string[]>([]);
  const busy = bulkTags.isPending || bulkLists.isPending || bulkSegments.isPending || bulkUpdate.isPending || bulkDelete.isPending;

  const run = async (action: BulkAction, fn: () => Promise<string>) => {
    try {
      message.success(await fn());
      setMode(null);
      setPicked([]);
      onDone(action);
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const n = count.toLocaleString();
  const people = plural(count, 'contact');
  const open = (m: Mode) => {
    setPicked([]);
    setMode(m);
  };
  const close = () => {
    setPicked([]);
    setMode(null);
  };

  return (
    <>
      <Space wrap>
        <Typography.Text strong>{n} selected</Typography.Text>
        <Button size="small" icon={<TagsOutlined />} onClick={() => open('addTags')}>Add tags</Button>
        <Button size="small" icon={<TagsOutlined />} onClick={() => open('removeTags')}>Remove tags</Button>
        <Button size="small" icon={<UnorderedListOutlined />} onClick={() => open('addList')}>Add to list</Button>
        <Button size="small" icon={<UnorderedListOutlined />} onClick={() => open('removeList')}>Remove from list</Button>
        <Button size="small" icon={<FilterOutlined />} onClick={() => open('addSegment')}>Add to segment</Button>
        <Button size="small" icon={<FilterOutlined />} onClick={() => open('removeSegment')}>Remove from segment</Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => open('setFields')}>Edit fields</Button>
        <Button size="small" icon={<SafetyCertificateOutlined />} loading={verifySel.isPending} onClick={() => void run('verify', async () => `Queued ${plural((await verifySel.mutateAsync({ selection, onlyUnverified: true })).total, 'contact')} for email verification`)}>Verify emails</Button>
        <Popconfirm title={`Delete ${people} and their history?`} description="This cannot be undone." okText="Delete" okButtonProps={{ danger: true, loading: bulkDelete.isPending }} onConfirm={() => run('delete', async () => `Deleted ${plural((await bulkDelete.mutateAsync(selection)).deleted, 'contact')}`)}>
          <Button size="small" danger icon={<DeleteOutlined />} loading={bulkDelete.isPending}>Delete</Button>
        </Popconfirm>
      </Space>

      <Modal
        title={mode === 'addTags' ? `Add tags to ${people}` : `Remove tags from ${people}`}
        open={mode === 'addTags' || mode === 'removeTags'}
        onCancel={close}
        okText={mode === 'addTags' ? 'Add tags' : 'Remove tags'}
        okButtonProps={{ disabled: !picked.length, loading: busy }}
        onOk={() => void run('tags', async () => {
          const r = await bulkTags.mutateAsync(mode === 'addTags' ? { selection, add: picked } : { selection, remove: picked });
          return mode === 'addTags' ? `Added ${plural(r.added ?? 0, 'tag assignment')}` : `Removed ${plural(r.removed ?? 0, 'tag assignment')}`;
        })}
        destroyOnHidden
      >
        <CreatableSelect
          multiple
          items={tags.data}
          value={picked}
          onChange={setPicked}
          placeholder={mode === 'addTags' ? 'Choose tags or type to create one' : 'Choose tags'}
          create={mode === 'addTags' ? { noun: 'tag', pending: createTag.isPending, run: async (name) => (await createTag.mutateAsync({ name })).id } : undefined}
        />
        {mode === 'removeTags' && tags.data && !tags.data.length && <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>No tags yet. <Link to="/tags">Create a tag</Link> first.</Typography.Paragraph>}
      </Modal>

      <Modal
        title={mode === 'addList' ? `Add ${people} to lists` : `Remove ${people} from lists`}
        open={mode === 'addList' || mode === 'removeList'}
        onCancel={close}
        okText={mode === 'addList' ? 'Add to lists' : 'Remove from lists'}
        okButtonProps={{ disabled: !picked.length, loading: busy }}
        onOk={() => void run('lists', async () => {
          const r = await bulkLists.mutateAsync(mode === 'addList' ? { selection, add: picked } : { selection, remove: picked });
          return mode === 'addList' ? `Added ${plural(r.added ?? 0, 'list membership')}` : `Removed ${plural(r.removed ?? 0, 'list membership')}`;
        })}
        destroyOnHidden
      >
        <CreatableSelect
          multiple
          items={lists.data}
          value={picked}
          onChange={setPicked}
          placeholder={mode === 'addList' ? 'Choose lists or type to create one' : 'Choose lists'}
          create={mode === 'addList' ? { noun: 'list', pending: createList.isPending, run: async (name) => (await createList.mutateAsync({ name })).id } : undefined}
        />
        {mode === 'removeList' && lists.data && !lists.data.length && <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>No lists yet. <Link to="/lists">Create a list</Link> first.</Typography.Paragraph>}
      </Modal>

      <Modal
        title={mode === 'addSegment' ? `Add ${people} to segments` : `Remove ${people} from segments`}
        open={mode === 'addSegment' || mode === 'removeSegment'}
        onCancel={close}
        okText={mode === 'addSegment' ? 'Add to segments' : 'Remove from segments'}
        okButtonProps={{ disabled: !picked.length, loading: busy }}
        onOk={() => void run('segments', async () => {
          const r = await bulkSegments.mutateAsync(mode === 'addSegment' ? { selection, add: picked } : { selection, remove: picked });
          return mode === 'addSegment' ? `Added ${plural(r.added ?? 0, 'segment membership')}` : `Removed ${plural(r.removed ?? 0, 'segment membership')}`;
        })}
        destroyOnHidden
      >
        <CreatableSelect
          multiple
          items={segments.data}
          value={picked}
          onChange={setPicked}
          placeholder={mode === 'addSegment' ? 'Choose segments or type to create one' : 'Choose segments'}
          create={mode === 'addSegment' ? { noun: 'segment', pending: createSegment.isPending, run: async (name) => (await createSegment.mutateAsync({ name, rules: { combinator: 'and', conditions: [] } })).id } : undefined}
        />
        <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          {mode === 'addSegment'
            ? 'Added contacts stay in the segment whatever its rules say.'
            : 'Removed contacts stay out of the segment even if they match its rules, until you add them back.'}
        </Typography.Paragraph>
        {mode === 'removeSegment' && segments.data && !segments.data.length && <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>No segments yet. <Link to="/segments/new">Create a segment</Link> first.</Typography.Paragraph>}
      </Modal>

      <MassEditModal
        open={mode === 'setFields'}
        count={count}
        contactFields={contactFields}
        loading={busy}
        onCancel={close}
        onApply={(data) => void run('fields', async () => `Updated ${plural((await bulkUpdate.mutateAsync({ selection, data })).updated, 'contact')}`)}
      />
    </>
  );
}
